import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useDragControls, type Variants } from "framer-motion";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";

import { Close } from "@chef/icons/small";
import { useIntercom } from "@chef/hooks";
import { Card } from "@chef/components";

import { BackToTopButton } from "@chef/smart-components";

import { intl } from "./Modal.Intl";

type Panel = "card" | "none";

type Size = "auto" | "sm" | "md" | "lg" | "xl" | "fullscreen";

type Mode = "mobile" | "desktop" | "unknown";

type Interaction =
  | "pull-up"
  | "pull-down"
  | "top-close-button"
  | "bottom-close-button"
  | "scroll-to-top";

type Interactions = [
  Interaction,
  "enabled" | "disabled" | "mobile-only" | "desktop-only",
][];

type Animation =
  | "none"
  | "fly-up"
  | "fly-down"
  | "fly-in-from-right"
  | "fly-in-from-left";

type ModalProps = {
  title?: string;
  size?: Size;
  panel?: Panel;
  onClose?: () => void;
  animation?: Animation;
  interactions?: Interactions;
  children: React.ReactNode;
  className?: string;
} & (
  | {
      isLoading: true;
      fallback: React.ReactNode;
    }
  | {
      isLoading?: false;
      fallback?: React.ReactNode;
    }
);

type PanelProps = {
  type: Panel;
  mode: Mode;
  direction: "up" | "down";
  children?: React.ReactNode;
};

const variants: Variants = {
  none: {},
  "fly-down": {
    y: "-40vh",
    opacity: 1,
  },
  "fly-up": {
    y: "40vh",
    opacity: 1,
  },
  "fly-in-from-right": {
    x: "100vw",
    opacity: 1,
  },
  "fly-in-from-left": {
    x: "-100vw",
    opacity: 1,
  },
};

const createInteractionsMap = (interactions: Interactions, mode: Mode) => {
  const interactionsMap = new Map<Interaction, boolean>();

  for (const [interaction, status] of interactions) {
    if (status === "disabled") {
      interactionsMap.set(interaction, false);
    } else if (status === "mobile-only" && mode === "mobile") {
      interactionsMap.set(interaction, true);
    } else if (status === "desktop-only" && mode === "desktop") {
      interactionsMap.set(interaction, true);
    } else if (status === "enabled") {
      interactionsMap.set(interaction, true);
    } else {
      interactionsMap.set(interaction, false);
    }
  }

  return interactionsMap;
};

const Panel = ({ type, mode, direction, children }: PanelProps) => {
  switch (type) {
    case "card":
      return (
        <Card
          noRounding
          noShadow
          className={clsx("shadow-xl", {
            "rounded-4xl": mode === "desktop",
            "rounded-t-4xl": mode === "mobile" && direction === "up",
            "rounded-b-4xl": mode === "mobile" && direction === "down",
          })}
        >
          {children}
        </Card>
      );
    case "none":
      return children;
    default:
      return null;
  }
};

export const Modal = ({
  isLoading = false,
  size = "lg",
  panel = "none",
  interactions = [],
  animation = "none",
  onClose,
  children,
  fallback,
  className,
}: ModalProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const { hideBubble, showBubble } = useIntercom();

  const [isAtTop, setIsAtTop] = useState(true);
  const [mode, setMode] = useState<Mode>("unknown");

  const dragControls = useDragControls();

  const interactionsMap = createInteractionsMap(interactions, mode);

  const ref = useCallback((node: HTMLDivElement | null) => {
    const onResize = () => {
      if (!node) {
        setMode("unknown");
        return;
      }

      setMode(window.innerWidth === node.clientWidth ? "mobile" : "desktop");
    };

    if (node) {
      setTimeout(onResize, 0);
      window.addEventListener("resize", onResize);
    } else {
      window.removeEventListener("resize", onResize);
    }
  }, []);

  useEffect(() => {
    hideBubble();

    return () => showBubble();
  }, [hideBubble, showBubble]);

  const handleClose = () => {
    onClose?.();
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    setIsAtTop(target.scrollTop === 0);
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (
      !isAtTop ||
      (!interactionsMap.get("pull-down") && !interactionsMap.get("pull-up"))
    ) {
      return;
    }

    dragControls.start(e);
  };

  return (
    <Dialog open onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/35 backdrop-blur-sm" />
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="fixed inset-0 w-screen overflow-y-auto"
      >
        {isLoading ? (
          fallback
        ) : (
          <div
            className={clsx(
              "flex flex-col min-h-full",
              {
                "mx-4": animation === "none" && size !== "fullscreen",
                "items-center justify-start py-16":
                  mode === "desktop" || animation === "none",

                "items-center justify-end":
                  animation === "fly-up" && mode !== "desktop",
                "items-center justify-start":
                  animation === "fly-down" && mode !== "desktop",
                "items-end justify-center":
                  animation === "fly-in-from-right" && mode !== "desktop",
                "items-start justify-center":
                  animation === "fly-in-from-left" && mode !== "desktop",
              },
              className,
            )}
          >
            <DialogPanel
              as={motion.div}
              variants={variants}
              animate={{
                y: 0,
                opacity: 1,
                transition: {
                  type: "spring",
                  stiffness: 400,
                  damping: 20,
                  mass: 0.15,
                },
              }}
              initial={animation}
              className={clsx("relative w-full", {
                "max-w-screen-sm": size === "sm",
                "max-w-screen-md": size === "md",
                "max-w-screen-lg": size === "lg",
                "max-w-screen-xl": size === "xl",
                "h-screen": size === "fullscreen",
              })}
            >
              <motion.div
                drag="y"
                dragListener={false}
                dragControls={dragControls}
                dragElastic={0.3}
                dragConstraints={{ top: 0, bottom: 0 }}
                onPointerDown={handlePointerDown}
                onDragStart={(_, info) => {
                  // Cancel the drag gesture if the user is pulling in the scroll direction
                  // to allow the native scroll to take over.
                  if (interactionsMap.get("pull-down") && info.delta.y < 0) {
                    dragControls.cancel();
                  } else if (
                    interactionsMap.get("pull-up") &&
                    info.delta.y > 0
                  ) {
                    dragControls.cancel();
                  }
                }}
                onDragEnd={(_, info) => {
                  // Close the modal if dragged more than 75px
                  if (Math.abs(info.offset.y) > 75) {
                    handleClose();
                  }
                }}
                ref={ref}
                className="w-full h-full"
                style={{
                  // Prevent upward touch interaction when at the top
                  // to allow drag controls to work properly.
                  touchAction: isAtTop ? "pan-down" : "pan-y",
                }}
              >
                {interactionsMap.get("pull-down") && (
                  <div className="absolute top-0 z-20 flex items-start justify-center w-full">
                    <div className="w-32 h-1 my-1 bg-black rounded" />
                  </div>
                )}
                {interactionsMap.get("top-close-button") && (
                  <button
                    onClick={onClose}
                    className="absolute top-0 right-0 z-20 flex items-center justify-center w-12 h-12 bg-white rounded-tr-4xl rounded-bl-4xl"
                    aria-label={intl.CLOSE}
                  >
                    <Close className="w-4 h-4" />
                  </button>
                )}
                <Panel
                  type={panel}
                  mode={mode}
                  direction={animation === "fly-down" ? "down" : "up"}
                >
                  {children}
                </Panel>
                {interactionsMap.get("pull-up") && (
                  <div className="absolute bottom-0 z-20 flex items-start justify-center w-full">
                    <div className="w-32 h-1 my-1 bg-black rounded" />
                  </div>
                )}
              </motion.div>
              {interactionsMap.get("scroll-to-top") && (
                <BackToTopButton
                  element={scrollRef.current}
                  sticky={false}
                  className="my-4"
                />
              )}
            </DialogPanel>
            {interactionsMap.get("bottom-close-button") && (
              <button className="flex items-center self-center justify-center w-12 h-12 my-4 rounded-full frosted-glass">
                <Close className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
};
